import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn, paginationRange } from "@/lib/utils";

interface PaginationProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** Total item count across all pages. Pairs with `pageSize` to render the "Showing X-Y of Z" summary. */
  total?: number;
  /** Items per page — the same limit passed to the list API call. */
  pageSize?: number;
  /** Plural noun for the summary text, e.g. "students". Defaults to "items". */
  itemLabel?: string;
  className?: string;
}

const Pagination: React.FC<PaginationProps> = ({
  page,
  totalPages,
  onPageChange,
  total,
  pageSize,
  itemLabel = "items",
  className,
}) => {
  const showSummary = total !== undefined && pageSize !== undefined && total > 0;
  const showButtons = totalPages > 1;
  if (!showSummary && !showButtons) return null;

  return (
    <div className={cn("flex flex-col sm:flex-row items-center justify-between gap-3", className)}>
      {showSummary ? (
        <p className="text-sm text-gray-600">
          Showing{" "}
          <span className="font-medium">{(page - 1) * pageSize! + 1}</span>
          {"–"}
          <span className="font-medium">{Math.min(page * pageSize!, total!)}</span>{" "}
          of <span className="font-medium">{total}</span> {itemLabel}
        </p>
      ) : (
        <div />
      )}
      {showButtons && (
        <div className="flex flex-wrap items-center justify-center gap-1">
          <button
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page === 1}
            aria-label="Previous page"
            title="Previous page"
            className="p-1.5 border border-gray-300 rounded disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          {paginationRange(page, totalPages).map((p, i) =>
            p === null ? (
              <span key={`gap-${i}`} className="px-2 py-1 text-sm text-gray-400">
                …
              </span>
            ) : (
              <button
                key={p}
                onClick={() => onPageChange(p)}
                aria-current={p === page ? "page" : undefined}
                className={cn(
                  "px-3 py-1 text-sm border rounded",
                  p === page
                    ? "border-primary bg-primary text-white"
                    : "border-gray-300 hover:bg-gray-50",
                )}
              >
                {p}
              </button>
            ),
          )}
          <button
            onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            aria-label="Next page"
            title="Next page"
            className="p-1.5 border border-gray-300 rounded disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default Pagination;
